import { useMutation } from "@apollo/client/react";
import { DSModal } from "@components/design-system/organisms";
import mixpanel from "mixpanel-browser";

import { SelectedNoteCard } from "./SelectedNoteCard";
import { defaultErrorHandler } from "../../../utils/operationTracking";
import { DETACH_NOTES_FROM_PATIENT } from "../../notes/edit/api/mutations";
import { alert } from "../Alert";

export function UnassignFromPatientModal({
  isOpen,
  onClose,
  notes,
  refetch,
  onSuccess,
}) {
  const [detachNotesFromPatient, { loading }] = useMutation(
    DETACH_NOTES_FROM_PATIENT,
  );

  const numNotes = notes?.length ?? 0;
  const isPlural = numNotes > 1;

  function handleConfirm() {
    const noteUuids = notes.map((note) => note.uuid);
    detachNotesFromPatient({ variables: { noteUuids } })
      .then(({ data }) => {
        const affected =
          data?.detachNotesFromPatient?.affectedCount ?? numNotes;
        mixpanel.track("Notes Unassigned From Patient", {
          affectedCount: affected,
        });
        alert(
          "success",
          `Unassigned ${affected} note${
            affected === 1 ? "" : "s"
          } from patient.`,
        );
        onClose();
        onSuccess?.();
        refetch?.();
      })
      .catch(defaultErrorHandler);
  }

  return (
    <DSModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isPlural
          ? "Unassign notes from patient?"
          : "Unassign note from patient?"
      }
      dataTestId="unassign-from-patient-modal"
      actions={[
        {
          label: "Cancel",
          variant: "cancel",
          onClick: onClose,
        },
        {
          label: "Unassign",
          variant: "primary",
          onClick: handleConfirm,
          disabled: loading || numNotes === 0,
          dataTestId: "confirm-button",
        },
      ]}
    >
      <h5>
        Are you sure you&apos;d like to{" "}
        <strong>
          unassign{" "}
          {isPlural ? `these ${numNotes} notes` : "this note"}
        </strong>{" "}
        from their patient?
      </h5>
      {numNotes > 0 && (
        <div className="mt-4 max-h-64 overflow-y-auto">
          {notes.map((note) => (
            <SelectedNoteCard key={note?.uuid} note={note} />
          ))}
        </div>
      )}
    </DSModal>
  );
}
